import { useState } from "react";
import { useQueryClient } from "react-query";
import { IOfficeAppliance } from "../type";
import { FormProvider, useForm } from "react-hook-form";
import CancelButton from "components/CancelButton";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import SubmitHookForm from "components/SubmitHookForm";
import { toast } from "components/toastNotification";
import useAddOfficeAppliance from "../hooks/useAddOfficeAppliance";
import useUpdateOfficeAppliance from "../hooks/useUpdateOfficeAppliance";
import OfficeApplianceFormDetails from "./OfficeApplianceFormDetails";
import { useRecoilState, useRecoilValue } from "recoil";
import { showApplianceFromOffice } from "../atom";
import { OfficeTabs } from "modules/OfficeTab/OfficeTabs";
import { useHistory } from "react-router-dom";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { mainSelectedTab } from "modules/CarrierAgreementMgmt/atoms";

export const OfficeApplianceForm = ({
  isAddState,
  isEditState,
  setIsAddState,
  setIsEditState,
  rowForEdit,
  setRowForEdit,
}) => {
  //#region States
  const [newOfficeAppliance, setNewOfficeAppliance] =
    useState<IOfficeAppliance>();
  const [saveModal, setSaveModal] = useState(false);
  const [showFromOffice, setShowApplianceFromOffice] = useRecoilState(
    showApplianceFromOffice
  );
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  //#endregion

  //#region History and Permission
  const history = useHistory();
  const userPermissions = useRecoilValue(permissionsSelector);
  //#endregion

  //#region Handle Link
  const handleLinkOffice = (moduleName) => {
    const url = `/officesettings/${moduleName}`;
    history.push(moduleName);
    let userTabs = [
      {
        to: "",
      },
    ];
    for (const tab of OfficeTabs) {
      if (userPermissions.includes(tab.permission)) {
        userTabs.push({ to: tab.to });
      }
    }

    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };
  //#endregion

  //#region Form methods
  const useFormMethods = useForm<IOfficeAppliance>({
    defaultValues: {
      ...rowForEdit,
      purchaseDate: rowForEdit?.purchaseDate
        ? new Date(rowForEdit.purchaseDate).toISOString().split("T")[0]
        : null,
    },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;
  //#endregion

  //#region QueryClient
  const queryClient = useQueryClient();
  //#endregion

  //#region Add Office Appliance
  const {
    mutate: addOfficeAppliance,
    isSuccess: addSuccess,
    isLoading: addLoading,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddOfficeAppliance();
  //#endregion

  //#region Update Office Appliance
  const {
    mutate: updateOfficeAppliance,
    isSuccess: updateSuccess,
    isLoading: updateLoading,
    isError: updateError,
    data: updateResult,
    reset: resetUpdateData,
  } = useUpdateOfficeAppliance();
  //#endregion

  //#region OnSubmit
  const onSubmit = (data) => {
    setNewOfficeAppliance({
      ...data,
      isActive: isAddState ? true : useFormMethods.watch("isActive"),
    });

    if (isAddState) {
      setSaveModal(true);
    } else if (isEditState && isDirty) {
      setSaveModal(true);
    } else if (!isDirty) {
      toast.fire("Saving Failed", "No Changes have been made", "warning");
    }
  };
  //#endregion

  //#region Handle Save
  const handleSave = () => {
    setSaveModal(false);
    if (isAddState) {
      addOfficeAppliance(newOfficeAppliance);
    } else {
      updateOfficeAppliance(newOfficeAppliance);
    }
  };
  //#endregion

  //#region Handle Cancel
  const handleCancel = () => {
    if (showFromOffice) {
      setShowApplianceFromOffice(false);
      handleLinkOffice("officesetting");
    }
    setSaveModal(false);
    setIsAddState(false);
    setIsEditState(false);
    setRowForEdit(undefined);
  };
  //#endregion

  //#region Add Office Appliance Status
  if (addSuccess && addResult) {
    if (addResult.data === "Duplicate") {
      toast.fire("Failed", "Office Appliance already exists.", "error");
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Office Appliance added successfully.", "success");
      queryClient.invalidateQueries("GetOfficeAppliance");
      handleCancel();
      setIsAddState(false);
    } else if (addResult?.status === 500) {
      toast.fire(
        "Failed",
        "Failed to add Office Appliance! No data saved.",
        "error"
      );
      queryClient.invalidateQueries("GetOfficeAppliance");
      setIsAddState(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to add Office Appliance! No data saved.",
        "error"
      );
      queryClient.invalidateQueries("GetOfficeAppliance");
      setIsAddState(false);
    }
    resetAddData();
    setSaveModal(false);
  }
  if (addError) {
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to add Office Appliance! No data saved.",
      "error"
    );
    queryClient.invalidateQueries("GetOfficeAppliance");
    setIsAddState(false);
  }
  //#endregion

  //#region Update Office Appliance Status
  if (updateSuccess && updateResult) {
    if (updateResult.data === "Duplicate") {
      toast.fire("Failed", "Office Appliance already exists.", "error");
      setSaveModal(false);
    } else if (updateResult.status === 201 || updateResult.status === 200) {
      toast.fire(
        "Success",
        "Office Appliance updated successfully.",
        "success"
      );
      queryClient.invalidateQueries("GetOfficeAppliance");
      handleCancel();
      setIsEditState(false);
    } else if (updateResult?.status === 500) {
      toast.fire(
        "Failed",
        "Failed to updated Office Appliance! No data saved.",
        "error"
      );
      queryClient.invalidateQueries("GetOfficeAppliance");
      setIsEditState(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to updated Office Appliance! No data saved.",
        "error"
      );
      queryClient.invalidateQueries("GetOfficeAppliance");
      setIsEditState(false);
    }
    resetUpdateData();
    setSaveModal(false);
  }
  if (updateError) {
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to updated Office Appliance! No data saved.",
      "error"
    );
    queryClient.invalidateQueries("GetOfficeAppliance");
    setIsEditState(false);
  }
  //#endregion

  return (
    <>
      <div className="p-3 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-2">
              <div className="font-bold text-primary text-base">
                {isAddState ? "Add Office Appliance" : "Edit Office Appliance"}
              </div>
              <div className="flex justify-between gap-2">
                <SubmitHookForm
                  type="submit"
                  disabled={addLoading || updateLoading}
                  buttonText={addLoading || updateLoading ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={() => {
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div className="">
            <OfficeApplianceFormDetails isEditState={isEditState} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState ? "Add Office Appliance" : "Edit Office Apppliance"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
          }}
          onClickFunction={() => {
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
